import {
  CameraOutlined,
  CloseOutlined,
  LockFilled,
  UserOutlined
} from "@ant-design/icons"
import { Avatar, Button, Form, Select, Typography, Upload } from "antd"
import { type FormInstance } from "antd/es/form/hooks/useForm"
import { type UploadChangeParam } from "antd/es/upload/interface"
import React, { type ComponentProps, useState } from "react"

import { PhoneInput } from "../../../../../components/PhoneInputs/PhoneInput.tsx"
import { placeholders } from "../../../../../constants/placeholders"
import { validateFile } from "../../../../../utils/file-validation.ts"
import { normalizeEmail } from "../../../../../utils/form/normalizeEmail.tsx"
import { getBase64 } from "../../../../../utils/utils"
import { validatePhone } from "../../../../../utils/validation/validate-phone.ts"
import { type SettingProfileFormData } from "../../../types/types.ts"
import { TextInput } from "../../../../../components/Input/TextInput.tsx"

const { Text } = Typography

const MAX_SIZE_MB = 10
const ALLOWED_FILE_EXTENSIONS = ["jpeg", "jpg", "png"]

interface PersonalInformationFormSectionProps {
  form: FormInstance<SettingProfileFormData>
  initialValues: SettingProfileFormData
}

export function PersonalInformationFormSection({
  form,
  initialValues
}: PersonalInformationFormSectionProps) {
  const [avatarUrl, setAvatarUrl] = useState<string | null>(
    initialValues?.avatar && typeof initialValues?.avatar === "string"
      ? initialValues?.avatar
      : null
  )
  const watchAvatar = Form.useWatch("avatar", form)
  const avatarSrc =
    typeof watchAvatar === "string" || !watchAvatar ? watchAvatar : avatarUrl

  const handleAvatarChange = (info: UploadChangeParam) => {
    const { file } = info

    if (file.status === "done") {
      getBase64(file.originFileObj, (imageUrl: string) => {
        setAvatarUrl(imageUrl)
        form.setFieldsValue({ avatar: file.originFileObj })
      })
    }
  }

  const handleDeleteAvatar = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    setAvatarUrl(null)
    form.setFieldsValue({ avatar: null })
  }

  const beforeUpload = (file: File) => {
    return validateFile(file, {
      maxSize: MAX_SIZE_MB,
      allowedExtensions: ALLOWED_FILE_EXTENSIONS,
      defaultResult: true,
      sizeMessage: `This avatar is larger than ${MAX_SIZE_MB}MB. Please choose a smaller file.`
    })
  }

  const uploadProps: ComponentProps<typeof Upload> = {
    name: "avatar",
    showUploadList: false,
    multiple: false,
    accept: ALLOWED_FILE_EXTENSIONS.map((ext) => `.${ext}`).join(","),
    maxCount: 1,
    beforeUpload: beforeUpload,
    onChange: handleAvatarChange,
    customRequest: ({ onSuccess }) => onSuccess && setTimeout(onSuccess, 300)
  }

  return (
    <>
      {/* Profile Picture */}
      <div className="flex items-center gap-4 pb-4">
        <Form.Item noStyle name="avatar">
          <Upload {...uploadProps}>
            <div className="relative">
              <Avatar size={64} icon={<UserOutlined />} src={avatarSrc} />

              {avatarSrc ? (
                <Button
                  onClick={handleDeleteAvatar}
                  size={"small"}
                  className={"absolute -bottom-1 -right-1 rounded-full"}
                  icon={<CloseOutlined />}
                />
              ) : (
                <Button
                  type={"primary"}
                  className={"absolute -bottom-1 -right-1 rounded-full"}
                  icon={<CameraOutlined />}
                />
              )}
            </div>
          </Upload>
        </Form.Item>
        <div>
          <div className="font-medium">Profile Picture</div>
          <div className="text-xs text-gray-500">
            Click to upload a new photo
          </div>
          <Text type={"secondary"} className={"text-xs"}>
            {ALLOWED_FILE_EXTENSIONS?.join(", ")?.toUpperCase()} • Up to{" "}
            {MAX_SIZE_MB}MB • Recommended: 512×512 (square)
          </Text>
        </div>
      </div>

      <Form.Item<SettingProfileFormData>
        label={"First Name"}
        name="first_name"
        rules={[{ required: true, message: "Please enter your first name" }]}
      >
        <TextInput placeholder="First Name" />
      </Form.Item>
      <Form.Item<SettingProfileFormData>
        label={"Middle Name"}
        name="middle_name"
      >
        <TextInput placeholder="Middle Name" />
      </Form.Item>
      <Form.Item<SettingProfileFormData>
        label={"Last Name"}
        name="last_name"
        rules={[{ required: true, message: "Please enter your last name" }]}
      >
        <TextInput placeholder="Last Name" />
      </Form.Item>

      <Form.Item<SettingProfileFormData>
        label="Email"
        name="email"
        extra={"Linked to your login account"}
        rules={[
          {
            required: true,
            whitespace: true,
            message: "Please enter your email"
          },
          { type: "email", message: "Please enter a valid email" }
        ]}
        normalize={normalizeEmail}
      >
        <TextInput
          disableAutoCapitalize
          placeholder="example@ezbuild.com"
          prefix={<LockFilled />}
        />
      </Form.Item>

      <Form.Item<SettingProfileFormData>
        label={"Phone Number"}
        name="phone"
        rules={[{ required: true, validator: validatePhone }]}
      >
        <PhoneInput placeholder={placeholders.phone} />
      </Form.Item>

      <Form.Item label={"Role"}>
        <Select
          disabled
          placeholder="Select role"
          value={initialValues?.role}
        />
      </Form.Item>
    </>
  )
}
